import { Component, forwardRef, Input, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { QuillConfig, QuillModule } from 'ngx-quill';
import { QuillDefaultConfig } from 'src/app/shared/CovercySharedComponents.module';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'terra-image-enabled-quill-editor',
  templateUrl: './image-enabled-quill-editor.component.html',
  styleUrls: ['./image-enabled-quill-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, QuillModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageEnabledQuillEditorComponent),
      multi: true,
    }
  ]
})
export class ImageEnabledQuillEditorComponent implements OnInit, ControlValueAccessor {
  @Input() placeholder: string = '';
  @Input() format: string = 'html';
  @Input() additionalClasses: string = '';
  @Input() id: string;
  @Input() uploader?: any;
  
  // Create custom Quill config with image support
  quillConfig: QuillConfig;
  
  // ControlValueAccessor implementation
  value: any = '';
  private onTouched: () => void = () => {};
  private onChange: (value: any) => void = () => {};
  disabled: boolean = false;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.createQuillConfig();
  }

  private createQuillConfig() {
    // Clone the default config
    const config = JSON.parse(JSON.stringify(QuillDefaultConfig))
    
    // Get toolbar container and ensure it's an array
    const toolbar = config.modules.toolbar;
    const container = Array.isArray(toolbar.container) ? toolbar.container : [];
    
    // Find the ai-actions array index
    let aiActionsIndex = -1;
    for (let i = 0; i < container.length; i++) {
      if (Array.isArray(container[i]) && container[i].some(item => 
        item && typeof item === 'object' && 'ai-actions' in item)) {
        aiActionsIndex = i;
        break;
      }
    }
    
    // Insert image button array before ai-actions
    if (aiActionsIndex > -1) {
      container.splice(aiActionsIndex, 0, ['image']);
      container.splice(aiActionsIndex, 0, ['imageupload']);
    } else {
      // If ai-actions not found, add image button at the end
      container.push(['image']);
      container.push(['imageupload']);
    }
    
    // Update the toolbar container
    config.modules.toolbar.container = container;

    this.quillConfig = {
      ...QuillDefaultConfig,
      modules: {
        ...QuillDefaultConfig.modules,
        toolbar: {
          ...(typeof QuillDefaultConfig.modules.toolbar === 'object' ? QuillDefaultConfig.modules.toolbar : {}),
          container: container,
          handlers: {
            ...QuillDefaultConfig.modules.handlers,
            'ai-actions': () => {},
            'imageupload': () => {
              if (this.uploader && typeof this.uploader.open === 'function') {
                this.uploader.open();
              } else {
                console.warn('UploaderBlockComponent reference not set or open() not available.');
              }
            }
          }
        },
      }
    };
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (value !== this.value) {
      this.value = value;
      // Trigger change detection to update the view
      this.cdr.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    // Trigger change detection to update the view
    this.cdr.markForCheck();
  }

  onContentChanged(event: any) {
    if (event.source === 'user') {
      this.value = event.html || event.text;
      this.onChange(this.value);
      this.onTouched();
    }
  }
}